// Mock data for UCP Readiness Checker

export const mockAnalysisResults = {
  overallScore: 42,
  storeName: "example-store.com",
  passed: 8,
  partial: 5,
  failed: 12,
  overview: "Your store has basic e-commerce setup but lacks critical UCP infrastructure for agentic commerce. No UCP profile detected, missing structured semantic data, and no custom capability extensions found. To compete in the AI-first commerce era, focus on implementing the UCP protocol and building brand-specific extensions.",
  topPriorities: [
    {
      id: 1,
      title: "Implement UCP Profile",
      description: "Publish a UCP profile at /.well-known/ucp declaring your capabilities and extensions for AI agent discovery."
    },
    {
      id: 2,
      title: "Build Semantic Product Catalog",
      description: "Transform product data into machine-readable formats using Schema.org and Shopify Standard Taxonomy."
    },
    {
      id: 3,
      title: "Create Capability Extensions",
      description: "Build custom extensions for loyalty programs, consultative selling, and complex bundle negotiation."
    },
    {
      id: 4,
      title: "Machine-Readable Policies",
      description: "Convert returns, shipping, and warranty policies into JSON-LD manifests for AI agent parsing."
    },
    {
      id: 5,
      title: "Implement Landed Cost Guarantee",
      description: "Configure checkout to provide pre-calculated, guaranteed total prices to AI agents."
    }
  ],
  categories: [
    {
      id: "basic-setup",
      name: "Basic Setup",
      description: "Core technical requirements for AI discoverability",
      score: 33,
      passed: 1,
      partial: 1,
      failed: 1,
      checks: [
        {
          name: "UCP Profile (/.well-known/ucp)",
          status: "failed",
          priority: 1,
          description: "No UCP profile found. AI agents cannot discover your capabilities or negotiate transactions."
        },
        {
          name: "llms.txt File",
          status: "partial",
          priority: 2,
          description: "Basic llms.txt found but missing capability declarations and extension references."
        },
        {
          name: "AI Crawler Access",
          status: "passed",
          priority: 3,
          description: "robots.txt allows AI crawlers. Site is accessible to Googlebot and other AI agents."
        }
      ]
    },
    {
      id: "semantic-layer",
      name: "Semantic Layer",
      description: "Machine-readable product data for AI discovery",
      score: 35,
      passed: 2,
      partial: 1,
      failed: 3,
      checks: [
        {
          name: "Schema.org Product Markup",
          status: "partial",
          priority: 1,
          description: "Basic product schema found but missing key attributes like skin type, active ingredients, and SPF levels."
        },
        {
          name: "Shopify Standard Taxonomy",
          status: "failed",
          priority: 1,
          description: "Products not mapped to Shopify Standard Taxonomy. AI cannot categorize or compare your products."
        },
        {
          name: "Structured Attribute Mapping",
          status: "failed",
          priority: 1,
          description: "Product attributes buried in marketing copy. Need dedicated fields for filterable properties."
        },
        {
          name: "Metafield Configuration",
          status: "failed",
          priority: 2,
          description: "No custom metafields for product specifications. AI cannot extract technical details."
        },
        {
          name: "Product Identifiers (GTIN/SKU)",
          status: "passed",
          priority: 3,
          description: "GTINs and SKUs properly configured for product identification."
        },
        {
          name: "Inventory Data",
          status: "passed",
          priority: 3,
          description: "Real-time inventory levels available in structured data."
        }
      ]
    },
    {
      id: "gxo-signals",
      name: "GXO Signals",
      description: "Generative Experience Optimisation for AI trust",
      score: 25,
      passed: 1,
      partial: 1,
      failed: 3,
      checks: [
        {
          name: "Landed Cost Guarantee",
          status: "failed",
          priority: 1,
          description: "No pre-calculated total price available. AI agents cannot guarantee final costs to buyers."
        },
        {
          name: "Machine-Readable Returns Policy",
          status: "failed",
          priority: 1,
          description: "Returns policy in human-readable format only. Need JSON-LD manifest for AI parsing."
        },
        {
          name: "Machine-Readable Shipping Policy",
          status: "failed",
          priority: 1,
          description: "Shipping information not structured. AI cannot calculate delivery windows or costs."
        },
        {
          name: "Trust Signals",
          status: "partial",
          priority: 2,
          description: "Some reviews present but not consistently structured across products."
        },
        {
          name: "Business Verification",
          status: "passed",
          priority: 3,
          description: "Business details verified and consistent across platforms."
        }
      ]
    },
    {
      id: "ucp-extensions",
      name: "UCP Extensions",
      description: "Custom capability agents for brand differentiation",
      score: 0,
      passed: 0,
      partial: 0,
      failed: 4,
      checks: [
        {
          name: "Loyalty Negotiator Extension",
          status: "failed",
          priority: 1,
          description: "No loyalty extension found. AI cannot offer point redemption or member discounts during checkout."
        },
        {
          name: "Consultative Escalation Agent",
          status: "failed",
          priority: 1,
          description: "No custom consultation logic. AI cannot hand off complex queries to brand expertise."
        },
        {
          name: "Complex Bundle Negotiator",
          status: "failed",
          priority: 1,
          description: "No bundle extension found. AI cannot process build-your-own offers or kit configurations."
        },
        {
          name: "Custom Capability Declaration",
          status: "failed",
          priority: 1,
          description: "No custom capabilities declared. Your brand logic is invisible to AI agents."
        }
      ]
    },
    {
      id: "site-signals",
      name: "Site-wide Signals",
      description: "Trust and clarity signals across your store",
      score: 58,
      passed: 3,
      partial: 1,
      failed: 1,
      checks: [
        {
          name: "FAQ Content",
          status: "partial",
          priority: 2,
          description: "Limited FAQ coverage. Missing pre-purchase questions and product-specific FAQs."
        },
        {
          name: "Contact Information",
          status: "passed",
          priority: 2,
          description: "Contact page with email and form. AI can direct customer service inquiries."
        },
        {
          name: "About Page",
          status: "passed",
          priority: 3,
          description: "Brand story and values clearly communicated."
        },
        {
          name: "Social Proof",
          status: "passed",
          priority: 3,
          description: "Social media presence linked and active."
        },
        {
          name: "Structured Navigation",
          status: "failed",
          priority: 2,
          description: "Navigation not optimized for AI crawling. Need breadcrumbs and category hierarchy."
        }
      ]
    },
    {
      id: "product-pages",
      name: "Product Pages",
      description: "Product information quality and structure",
      score: 42,
      passed: 2,
      partial: 2,
      failed: 2,
      checks: [
        {
          name: "Technical Specifications",
          status: "failed",
          priority: 1,
          description: "Specs buried in descriptions. Need structured tables for AI extraction."
        },
        {
          name: "Product FAQs",
          status: "failed",
          priority: 1,
          description: "No product-specific FAQs. AI cannot answer common buyer questions."
        },
        {
          name: "Cross-sells & Upsells",
          status: "partial",
          priority: 2,
          description: "Basic recommendations present but not structured for AI bundle suggestions."
        },
        {
          name: "Variant Information",
          status: "partial",
          priority: 2,
          description: "Variants listed but missing comparison guidance for AI recommendations."
        },
        {
          name: "Product Images",
          status: "passed",
          priority: 3,
          description: "High-quality images with alt text for AI understanding."
        },
        {
          name: "Pricing Clarity",
          status: "passed",
          priority: 3,
          description: "Clear pricing with proper schema markup."
        }
      ]
    }
  ]
};

export const ucpPhases = [
  {
    phase: 1,
    title: "The Semantic Layer",
    subtitle: "The Foundation",
    problem: "AI agents cannot 'read' marketing copy. If your product benefits are buried in a paragraph, the AI will ignore them.",
    deliverable: "A Queryable Semantic Catalogue",
    features: [
      "Structured Attribute Mapping using Schema.org and Shopify Standard Taxonomy",
      "Custom metafield configuration for filterable properties",
      "Machine-readable product specifications"
    ],
    outcome: "Your products are 100% discoverable by Gemini, ChatGPT, and Microsoft Copilot."
  },
  {
    phase: 2,
    title: "GXO",
    subtitle: "Generative Experience Optimisation",
    problem: "AI agents only recommend merchants they trust. They look for specific 'signals' that standard SEO doesn't cover.",
    deliverable: "A UCP-Optimised Trust Profile",
    features: [
      "Landed Cost Guarantee configuration",
      "Machine-readable policies in JSON-LD",
      "Trust signal optimisation"
    ],
    outcome: "Your store is flagged as 'Low Friction' by AI agents, giving you a higher win rate."
  },
  {
    phase: 3,
    title: "UCP Extensions",
    subtitle: "The Growth Engine",
    problem: "Shopify handles the 'Buy' button, but it doesn't know your specific brand logic. Without custom extensions, the AI can't handle loyalty points or consultations.",
    deliverable: "Custom Capability Agents",
    features: [
      "Loyalty Negotiator for point redemption",
      "Consultative Escalation for expert handoff",
      "Complex Bundle Negotiator for custom offers"
    ],
    outcome: "Without extensions, you're just a product on a shelf. With them, you're a destination."
  }
];

export const partnerLogos = [
  { name: "Google", category: "codeveloped" },
  { name: "Shopify", category: "codeveloped" },
  { name: "Target", category: "codeveloped" },
  { name: "Walmart", category: "codeveloped" },
  { name: "Etsy", category: "codeveloped" },
  { name: "Wayfair", category: "codeveloped" }
];
